// Tenantra Inc. - Admin: Promo codes (campaign codes mirrored to Stripe)
import { useEffect, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { formatDate } from '@/utils/date';

const field = 'h-8 text-xs rounded-none border-slate-300';
const PLANS = ['starter', 'professional', 'enterprise'];
const INTERVALS = [['monthly', 'Monthly'], ['6_month', '6-Month'], ['annual', 'Annual']];

const valueLabel = (p) => (p.type === 'percent' ? `${p.value}% off` : `$${Number(p.value).toFixed(2)} off`);

export const PromoCodesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [redemptions, setRedemptions] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/promo-codes');
      setItems(r.data.items);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/promo-codes/${p.id}`, { active: !p.active });
      toast.success(p.active ? `${p.code} deactivated` : `${p.code} reactivated`);
      load();
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    }
  };

  const showRedemptions = async (p) => {
    try {
      const r = await api.get(`/admin/promo-codes/${p.id}/redemptions`);
      setRedemptions({ promo: p, items: r.data.items });
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    }
  };

  return (
    <div data-testid="admin-promo-codes-panel">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Promo Codes</h1>
          <p className="text-slate-500 text-[11px]">Campaign codes landlords enter at checkout. Applied on top of plan discounts; mirrored to Stripe.</p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)} className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" data-testid="promo-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />New code
        </Button>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="promo-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Code</th>
              <th className="text-left px-3 py-2 font-medium">Discount</th>
              <th className="text-left px-3 py-2 font-medium">Applies to</th>
              <th className="text-left px-3 py-2 font-medium">Redemptions</th>
              <th className="text-left px-3 py-2 font-medium">Expires</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500" data-testid="promo-empty">No promo codes yet</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`promo-row-${p.code}`}>
                <td className="px-3 py-2">
                  <p className="font-mono font-semibold text-slate-900">{p.code}</p>
                  {p.description && <p className="text-slate-500">{p.description}</p>}
                </td>
                <td className="px-3 py-2 text-slate-900">{valueLabel(p)}<p className="text-slate-500">{p.duration_label}</p></td>
                <td className="px-3 py-2 text-slate-700 capitalize">
                  {(p.plans?.length ? p.plans.join(', ') : 'All plans')}<p className="text-slate-500">{p.intervals?.length ? p.intervals.map((i) => i.replace('_', '-')).join(', ') : 'All periods'}</p>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => showRedemptions(p)} className="text-teal-700 hover:underline" data-testid={`promo-redemptions-${p.code}`}>
                    {p.redemptions}{p.max_redemptions ? ` / ${p.max_redemptions}` : ''}
                  </button>
                </td>
                <td className="px-3 py-2 text-slate-500">{p.expires_at ? formatDate(p.expires_at) : 'Never'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block border px-1.5 py-0.5 text-[10px] ${p.active ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-slate-500 bg-slate-100 border-slate-200'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] rounded-none" onClick={() => toggle(p)} data-testid={`promo-toggle-${p.code}`}>
                    {p.active ? 'Deactivate' : 'Reactivate'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <PromoEditor onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {redemptions && <RedemptionsDrawer data={redemptions} onClose={() => setRedemptions(null)} />}
    </div>
  );
};

const PromoEditor = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ code: '', description: '', type: 'percent', value: '', duration: 'once', duration_months: '', plans: [], intervals: [], expires_at: '', max_redemptions: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        code: form.code, description: form.description || null, type: form.type, value: Number(form.value), duration: form.duration,
        duration_months: form.duration === 'repeating' ? Number(form.duration_months) : null, plans: form.plans, intervals: form.intervals,
        expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59').toISOString() : null,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      };
      const r = await api.post('/admin/promo-codes', body);
      toast.success(`${r.data.code} created`);
      onSaved();
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="promo-editor">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">New promo code</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100" aria-label="Close"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Code</Label>
            <Input value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="SPRING25" className={`${field} font-mono`} maxLength={24} data-testid="promo-code-input" />
            <p className="text-[10px] text-slate-500 mt-1">3-24 letters or numbers. Landlords type this at checkout.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Description (internal)</Label>
            <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Spring landlord association campaign" className={field} data-testid="promo-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger className={field} data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="percent">Percent off</SelectItem><SelectItem value="amount">Dollar amount off (CAD)</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">{form.type === 'percent' ? 'Percent' : 'Amount (CAD)'}</Label>
              <Input type="number" min="1" max={form.type === 'percent' ? 100 : undefined} value={form.value} onChange={(e) => set('value', e.target.value)} className={field} data-testid="promo-value-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Duration</Label>
              <Select value={form.duration} onValueChange={(v) => set('duration', v)}>
                <SelectTrigger className={field} data-testid="promo-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="once">First payment only</SelectItem>
                  <SelectItem value="repeating">First N months</SelectItem>
                  <SelectItem value="forever">Every renewal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.duration === 'repeating' && (
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Months</Label>
                <Input type="number" min="1" max="36" value={form.duration_months} onChange={(e) => set('duration_months', e.target.value)} className={field} data-testid="promo-months-input" />
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Plans (none selected = all)</Label>
            <div className="flex gap-1 mt-1">
              {PLANS.map((p) => (
                <button key={p} type="button" onClick={() => toggleIn('plans', p)} className={`px-2 h-7 text-xs border capitalize ${form.plans.includes(p) ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-testid={`promo-plan-${p}`}>{p}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Billing periods (none selected = all)</Label>
            <div className="flex gap-1 mt-1">
              {INTERVALS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => toggleIn('intervals', k)} className={`px-2 h-7 text-xs border ${form.intervals.includes(k) ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-testid={`promo-interval-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Expires (optional)</Label>
              <Input type="date" value={form.expires_at} onChange={(e) => set('expires_at', e.target.value)} className={field} data-testid="promo-expires-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Max redemptions (optional)</Label>
              <Input type="number" min="1" value={form.max_redemptions} onChange={(e) => set('max_redemptions', e.target.value)} className={field} data-testid="promo-max-input" />
            </div>
          </div>
          <Button onClick={save} disabled={busy || !form.code || !form.value} className="w-full h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded-none" data-testid="promo-save-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create code'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const RedemptionsDrawer = ({ data, onClose }) => (
  <div className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end" onClick={onClose}>
    <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="promo-redemptions-drawer">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900 font-mono">{data.promo.code} · {data.items.length} redemption{data.items.length === 1 ? '' : 's'}</h2>
        <button onClick={onClose} className="p-1 hover:bg-slate-100" aria-label="Close"><X className="w-4 h-4 text-slate-500" /></button>
      </div>
      <div className="px-5 py-4">
        {data.items.length === 0 ? <p className="text-xs text-slate-500">No redemptions yet</p> : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="text-left py-1">Landlord</th><th className="text-left py-1">Plan</th><th className="text-right py-1">Paid</th><th className="text-right py-1">When</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((r) => (
                <tr key={r.id}><td className="py-1.5 text-slate-900">{r.email || r.user_id}</td><td className="py-1.5 capitalize text-slate-700">{r.plan} · {(r.billing_interval || '').replace('_', '-')}</td><td className="py-1.5 text-right">${r.amount_total}</td><td className="py-1.5 text-right text-slate-500">{formatDate(r.created_at)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);
